import type { GroupChannel } from "@sendbird/chat/groupChannel";
import { getMessageTypeFromFile } from "@/lib/api/messages";
import { getTeenLanguageFromChannel } from "@/lib/sendbird/translation";
import type { VoiceRecorderStatus } from "@somethings-ui/web";
import type { AttentionQuote } from "./attention-message";
import type { AttentionComposerMode } from "./attention-composer-model";
import type { AttentionMessageType } from "./attention-analytics";
import {
  sendAttentionFile,
  sendAttentionGif,
  sendAttentionText,
  type AttentionFileMessageSender,
  type AttentionUserMessageSender,
  type PersistAttentionMessage,
} from "./attention-send-adapter";
import { prepareAttentionVoiceFile } from "./attention-voice-file";

export interface AttentionSendPlan {
  reset: () => void;
  run: () => Promise<unknown>;
  onSuccess: () => void;
}

export interface AttentionSendPlanVoice {
  status: VoiceRecorderStatus;
  recording: { blob: Blob } | null;
  elapsedMs: number;
  discard: () => void;
}

export interface BuildAttentionSendPlanInput {
  mode: AttentionComposerMode;
  draft: string;
  quote: AttentionQuote | null;
  channel: GroupChannel | null;
  currentUserId: string;
  imageFile: File | null;
  selectedGif: string | null;
  voice: AttentionSendPlanVoice;
  clearImage: () => void;
  clearGif: () => void;
  setDraft: (value: string) => void;
  sendUserMessage: AttentionUserMessageSender;
  sendFileMessage: AttentionFileMessageSender;
  persist: PersistAttentionMessage;
  transcodeToM4a: (input: Blob) => Promise<Blob>;
  trackMessageSend: (messageType: AttentionMessageType, hasQuote: boolean) => void;
  trackVoiceSend: (durationMs: number, hasQuote: boolean) => void;
}

export function buildAttentionSendPlan(
  input: BuildAttentionSendPlanInput,
): AttentionSendPlan | null {
  const {
    mode,
    draft,
    quote,
    channel,
    currentUserId,
    imageFile,
    selectedGif,
    voice,
    clearImage,
    clearGif,
    setDraft,
    sendUserMessage,
    sendFileMessage,
    persist,
    transcodeToM4a,
    trackMessageSend,
    trackVoiceSend,
  } = input;

  if (!channel) return null;

  const activeQuote = quote;
  const hasQuote = activeQuote !== null;

  switch (mode) {
    case "voice": {
      const recording = voice.recording;
      if (voice.status !== "recorded" || recording === null) return null;
      const durationMs = voice.elapsedMs;
      const sourceBlob = recording.blob;
      return {
        reset: () => voice.discard(),
        run: async () => {
          const file = await prepareAttentionVoiceFile(
            sourceBlob,
            transcodeToM4a,
          );
          return sendAttentionFile({
            channel,
            file,
            quote: activeQuote,
            durationMs,
            sendFileMessage,
            persist,
          });
        },
        onSuccess: () => trackVoiceSend(durationMs, hasQuote),
      };
    }
    case "image": {
      const file = imageFile;
      if (file === null) return null;
      const messageType: AttentionMessageType =
        getMessageTypeFromFile(file) === "VIDEO_MESSAGE" ? "video" : "image";
      return {
        reset: clearImage,
        run: () =>
          sendAttentionFile({
            channel,
            file,
            quote: activeQuote,
            sendFileMessage,
            persist,
          }),
        onSuccess: () => trackMessageSend(messageType, hasQuote),
      };
    }
    case "gif": {
      const url = selectedGif;
      if (url === null) return null;
      return {
        reset: clearGif,
        run: () =>
          sendAttentionGif({
            channel,
            url,
            quote: activeQuote,
            sendUserMessage,
            persist,
          }),
        onSuccess: () => trackMessageSend("gif", hasQuote),
      };
    }
    case "idle":
    case "emoji":
    case "suggestions": {
      const text = draft.trim();
      if (text.length === 0) return null;
      const teenLanguage = getTeenLanguageFromChannel(channel, currentUserId);
      return {
        reset: () => setDraft(""),
        run: () =>
          sendAttentionText({
            channel,
            text,
            quote: activeQuote,
            translationTargetLanguages: teenLanguage ? [teenLanguage] : null,
            sendUserMessage,
            persist,
          }),
        onSuccess: () => trackMessageSend("text", hasQuote),
      };
    }
    default: {
      const exhaustive: never = mode;
      return exhaustive;
    }
  }
}
